import * as THREE from "three";
import { BrickMasonry } from "../entities/brick_masonry.js";
import { PhysicsCube } from "../entities/physics_cube.js";
import { Renderer } from "../core/renderer.js";
import { SceneManager } from "../core/scene_manager.js";
import { PhysicsWorld } from "../physics/physics_world.js";
import { ScreenManager, Screens } from "../screen_manager/screen_manager.js";
import { StressTest } from "./stress_test.js";
import { FpsOverlay } from "./fps_overlay.js";

/**
 * Brick (procedural BrickMasonry) vs default plywood block, side by side.
 *
 *   brickBench.geometry()     — cold build ms, verts/tris/bytes per size, createMesh cost per instance
 *   brickBench.run("BRICK 1x1 9x6 x4")  — load a stress scene, then idle + autofire profiling
 *   brickBench.compare()      — every BRICK/PLY pair, one table at the end
 *
 * Every line is prefixed with [BRICKBENCH]; the last report sits in brickBench.last.
 */

const TAG = "[BRICKBENCH]";

// PhysicsCube.VARIANTS: 1 = brick, 9 = purple plywood (what cube variant 0 maps to in GameScreen).
const BRICK_VARIANT = 1;
const PLYWOOD_VARIANT = 9;

const SIZES: [ number, number ][] = [
  [ 1, 1 ], [ 1, 2 ], [ 1, 3 ], [ 1, 4 ], [ 1, 5 ],
  [ 2, 1 ], [ 3, 1 ], [ 4, 1 ], [ 5, 1 ], [ 2, 2 ],
];

const PAIRS: [ string, string ][] = [
  [ "BRICK 1x1 9x6 x4", "PLY 1x1 9x6 x4" ],
  [ "BRICK 3x1 9x6 x4", "PLY 3x1 9x6 x4" ],
  [ "BRICK 2x2 8x6 x4", "PLY 2x2 8x6 x4" ],
];

interface GeometryStats
{
  meshes: number;
  draws: number;
  tris: number;
  verts: number;
  geometries: number;
  materials: number;
  bytes: number;
}

interface RunOptions
{
  settle_ms?: number;
  idle_ms?: number;
  fire_ms?: number;
  /** Drop the BrickMasonry cache first, so the scene pays the procedural build. */
  cold?: boolean;
}

const sleep = ( ms: number ) => new Promise<void>( resolve => setTimeout( resolve, ms ) );
const nextFrame = () => new Promise<number>( resolve => requestAnimationFrame( resolve ) );
const round = ( value: number, digits = 2 ) => +value.toFixed( digits );

function geometryBytes( geometry: THREE.BufferGeometry ): number
{
  let bytes = geometry.index ? geometry.index.array.byteLength : 0;
  for ( const name of Object.keys( geometry.attributes ) )
    bytes += ( geometry.attributes[name] as THREE.BufferAttribute ).array.byteLength;
  return bytes;
}

function triangleCount( geometry: THREE.BufferGeometry ): number
{
  return ( geometry.index ? geometry.index.count : geometry.getAttribute( "position" ).count ) / 3;
}

/** Visible meshes under `root`; `filter` narrows it to the level blocks. */
function collect( root: THREE.Object3D, filter?: ( mesh: THREE.Mesh ) => boolean ): GeometryStats
{
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  const stats: GeometryStats = { meshes: 0, draws: 0, tris: 0, verts: 0, geometries: 0, materials: 0, bytes: 0 };

  root.traverseVisible( object =>
  {
    if ( !( object instanceof THREE.Mesh ) || ( filter && !filter( object ) ) )
      return;

    const geometry = object.geometry as THREE.BufferGeometry;
    const material_list = Array.isArray( object.material ) ? object.material : [ object.material ];
    stats.meshes++;
    stats.draws += Array.isArray( object.material ) ? Math.max( 1, geometry.groups.length ) : 1;
    stats.tris += triangleCount( geometry );
    stats.verts += geometry.getAttribute( "position" ).count;
    material_list.forEach( material => materials.add( material ) );

    if ( !geometries.has( geometry ) )
    {
      geometries.add( geometry );
      stats.bytes += geometryBytes( geometry );
    }
  } );

  stats.geometries = geometries.size;
  stats.materials = materials.size;
  return stats;
}

function cubeMeshes(): Set<THREE.Object3D>
{
  const set = new Set<THREE.Object3D>();
  const screen = ScreenManager.getCurrentScreenInstance() as any;
  const entities: unknown[] = screen?.entities ?? ScreenManager.getCurrentUi()?.entities ?? [];
  for ( const entity of entities )
  {
    if ( !( entity instanceof PhysicsCube ) )
      continue;
    ( entity as any ).mesh?.traverse( ( child: THREE.Object3D ) => set.add( child ) );
  }
  return set;
}

/** Wraps a private static so its calls are counted and timed until `restore()`. */
function timeStatic( owner: any, method: string ): { calls: number; ms: number; restore: () => void }
{
  const original = owner[method];
  const probe = {
    calls: 0,
    ms: 0,
    restore: () => { owner[method] = original; },
  };
  owner[method] = function ( this: unknown, ...args: unknown[] )
  {
    const t0 = performance.now();
    const result = original.apply( this, args );
    probe.ms += performance.now() - t0;
    probe.calls++;
    return result;
  };
  return probe;
}

/** Samples renderer.info across frames: draw calls and triangles actually submitted (shadow pass included). */
async function sampleRenderInfo( ms: number ): Promise<Record<string, number>>
{
  const info = Renderer.renderer?.info;
  if ( !info )
    return {};

  const calls: number[] = [];
  const tris: number[] = [];
  const end = performance.now() + ms;
  while ( performance.now() < end )
  {
    await nextFrame();
    calls.push( info.render.calls );
    tris.push( info.render.triangles );
  }

  const mean = ( values: number[] ) => values.length ? values.reduce( ( a, b ) => a + b, 0 ) / values.length : 0;
  return {
    frames: calls.length,
    calls_mean: round( mean( calls ), 1 ),
    calls_max: Math.max( 0, ...calls ),
    tris_mean: Math.round( mean( tris ) ),
    tris_max: Math.max( 0, ...tris ),
    gpu_geometries: info.memory.geometries,
    gpu_textures: info.memory.textures,
    programs: info.programs?.length ?? 0,
  };
}

async function profile( ms: number ): Promise<Record<string, any>>
{
  FpsOverlay.profileStart();
  const render = await sampleRenderInfo( ms );
  FpsOverlay.profileStop();
  const stats = FpsOverlay.profileStats() as Record<string, any>;
  return {
    fps: stats.fps,
    dt: stats.dt,
    phys: stats.phys?.mean,
    game: stats.game?.mean,
    gl: stats.gl?.mean,
    gl_p99: stats.gl?.p99,
    step_ms: stats.step?.mean,
    bodies: stats.bodies?.mean,
    awake: stats.awake?.mean,
    spikes: stats.spikes?.count,
    spike_pct: stats.spikes?.percent,
    ...render,
  };
}

export class BrickBench
{
  public static last: Record<string, unknown> | null = null;
  /** Every run() report since page load, in order. */
  public static history: Record<string, any>[] = [];

  /** Compact one-row-per-run view of history, printed with console.table. */
  public static table(): Record<string, unknown>[]
  {
    const rows = BrickBench.history.map( report => ( {
      scene: report.name,
      blocks: report.blocks,
      mesh_ms: report.create_mesh_ms,
      build_ms: report.masonry_build_ms,
      block_tris: report.block_tris,
      block_kb: report.block_kb,
      idle_fps: report.idle.fps,
      idle_gl: report.idle.gl,
      idle_calls: report.idle.calls_mean,
      idle_gpu_tris: report.idle.tris_mean,
      fire_fps: report.fire.fps,
      fire_p99: report.fire.dt?.p99,
      fire_gl: report.fire.gl,
      fire_gl_p99: report.fire.gl_p99,
      fire_phys: report.fire.phys,
      fire_gpu_tris: report.fire.tris_mean,
      fire_gpu_tris_max: report.fire.tris_max,
      fire_spikes: report.fire.spikes,
    } ) );
    console.table( rows );
    return rows;
  }

  /** Pure geometry: no scene, no physics. */
  public static geometry( iterations = 20, log = true ): Record<string, any>[]
  {
    const masonry = BrickMasonry as any;
    const cube = PhysicsCube as any;
    const variants = cube.VARIANTS;
    const rows: Record<string, unknown>[] = [];

    for ( const [ width, height ] of SIZES )
    {
      // Procedural build straight through build(), bypassing the cache: that is the cold cost a level pays per new size/layout.
      let build_ms = 0;
      let masonry_stats: GeometryStats | null = null;
      for ( let layout = 0; layout < masonry.LAYOUTS; layout++ )
      {
        const t0 = performance.now();
        const geometry: THREE.BufferGeometry = masonry.build( width, height, layout );
        build_ms += performance.now() - t0;
        masonry_stats ??= collect( new THREE.Mesh( geometry ) );
        geometry.dispose();
      }

      const measure = ( variant_index: number ) =>
      {
        const t_first = performance.now();
        const first: THREE.Object3D = cube.createMesh( width, height, variants[variant_index] );
        const first_ms = performance.now() - t_first;

        // Brick picks one of LAYOUTS at random; warm them all so the timed loop never hits a cold build.
        for ( let i = 0; i < iterations; i++ )
          cube.createMesh( width, height, variants[variant_index] );

        const t0 = performance.now();
        for ( let i = 0; i < iterations; i++ )
          cube.createMesh( width, height, variants[variant_index] );
        const warm_ms = ( performance.now() - t0 ) / iterations;

        return { first_ms, warm_ms, stats: collect( first ) };
      };

      const brick = measure( BRICK_VARIANT );
      const plywood = measure( PLYWOOD_VARIANT );

      rows.push( {
        size: `${width}x${height}`,
        brick_build_ms: round( build_ms / masonry.LAYOUTS ),
        // 44 triangles per chamfered brick, 12 for the mortar core.
        brick_bricks: masonry_stats ? Math.round( ( masonry_stats.tris - 12 ) / 44 ) : 0,
        brick_tris: brick.stats.tris,
        ply_tris: plywood.stats.tris,
        tris_ratio: round( brick.stats.tris / Math.max( 1, plywood.stats.tris ) ),
        brick_verts: brick.stats.verts,
        ply_verts: plywood.stats.verts,
        brick_kb: round( brick.stats.bytes / 1024, 1 ),
        ply_kb: round( plywood.stats.bytes / 1024, 1 ),
        brick_draws: brick.stats.draws,
        ply_draws: plywood.stats.draws,
        brick_mesh_first_ms: round( brick.first_ms ),
        brick_mesh_warm_ms: round( brick.warm_ms, 3 ),
        ply_mesh_first_ms: round( plywood.first_ms ),
        ply_mesh_warm_ms: round( plywood.warm_ms, 3 ),
      } );
    }

    if ( log )
    {
      console.log( `${TAG} geometry per block (brick = BrickMasonry, ply = default plywood)` );
      console.table( rows );
    }
    BrickBench.last = { kind: "geometry", rows };
    return rows;
  }

  public static async run( name: string, options: RunOptions = {} ): Promise<Record<string, unknown>>
  {
    const { settle_ms = 2500, idle_ms = 4000, fire_ms = 8000, cold = true } = options;
    const index = StressTest.indexOf( name );
    if ( index < 0 )
      throw new Error( `${TAG} unknown stress scene "${name}"` );

    if ( cold )
      ( BrickMasonry as any ).geometries.clear();

    StressTest.setAutofire( false );
    const create_mesh = timeStatic( PhysicsCube, "createMesh" );
    const masonry_build = timeStatic( BrickMasonry, "build" );

    const t0 = performance.now();
    let load_ms = 0;
    try
    {
      await ScreenManager.showScreen( Screens.GAME, { level: StressTest.activate( index ) } );
      const expected = StressTest.getBlockCount( index );
      const deadline = performance.now() + 15000;
      while ( cubeMeshes().size === 0 && performance.now() < deadline )
        await nextFrame();
      load_ms = performance.now() - t0;
      console.log( `${TAG} ${name}: loaded ${expected} blocks in ${round( load_ms, 0 )} ms` );
    }
    finally
    {
      create_mesh.restore();
      masonry_build.restore();
    }

    const cubes = cubeMeshes();
    const blocks = collect( SceneManager.scene, mesh => cubes.has( mesh ) );
    const scene = collect( SceneManager.scene );

    await sleep( settle_ms );
    console.log( `${TAG} ${name}: idle ${idle_ms} ms` );
    const idle = await profile( idle_ms );

    console.log( `${TAG} ${name}: autofire ${fire_ms} ms` );
    StressTest.setAutofire( true );
    const fire = await profile( fire_ms );
    StressTest.setAutofire( false );

    const report = {
      name,
      blocks: StressTest.getBlockCount( index ),
      load_ms: round( load_ms, 0 ),
      create_mesh_ms: round( create_mesh.ms ),
      create_mesh_calls: create_mesh.calls,
      create_mesh_avg_ms: round( create_mesh.ms / Math.max( 1, create_mesh.calls ), 3 ),
      masonry_builds: masonry_build.calls,
      masonry_build_ms: round( masonry_build.ms ),
      block_meshes: blocks.meshes,
      block_tris: blocks.tris,
      block_geometries: blocks.geometries,
      block_materials: blocks.materials,
      block_kb: round( blocks.bytes / 1024, 1 ),
      scene_tris: scene.tris,
      scene_meshes: scene.meshes,
      physics: PhysicsWorld.counts(),
      idle,
      fire,
    };

    console.log( `${TAG} ${name}`, report );
    BrickBench.last = report;
    BrickBench.history.push( report );
    return report;
  }

  private static running = false;
  private static status_el: HTMLDivElement | null = null;
  private static report_el: HTMLDivElement | null = null;
  /** Plain-text report of the last runAndShow(), also kept in localStorage. */
  public static last_text = "";
  private static readonly TEXT_KEY = "bb_brick_bench_last_v1";

  /**
   * One tap from the debug panel: runs every BRICK/PLY pair, then shows a plain
   * text report on screen (COPY / SAVE TXT) and prints it to the console once.
   * Nothing to type, nothing to collect from a remote inspector.
   */
  public static async runAndShow(): Promise<void>
  {
    if ( BrickBench.running )
      return;
    BrickBench.running = true;
    BrickBench.hideReport();

    const options: RunOptions = { settle_ms: 2500, idle_ms: 4000, fire_ms: 8000 };
    const names = PAIRS.flat();

    try
    {
      BrickBench.showStatus( "BRICK BENCH: geometry…" );
      await nextFrame();
      const geometry = BrickBench.geometry( 20, false );

      BrickBench.history = [];
      for ( let i = 0; i < names.length; i++ )
      {
        BrickBench.showStatus( `BRICK BENCH ${i + 1}/${names.length}: ${names[i]}\n(~15 s each, don't touch the screen)` );
        await BrickBench.run( names[i], options );
      }

      StressTest.deactivate();
      BrickBench.last_text = BrickBench.formatReport( geometry, BrickBench.history );
      try { localStorage.setItem( BrickBench.TEXT_KEY, BrickBench.last_text ); } catch { /* storage unavailable */ }
      console.log( BrickBench.last_text );
      BrickBench.showReport( BrickBench.last_text );
    }
    catch ( error )
    {
      console.error( `${TAG} failed`, error );
      BrickBench.showReport( `BRICK BENCH FAILED\n${( error as Error )?.stack ?? error}` );
    }
    finally
    {
      StressTest.setAutofire( false );
      BrickBench.hideStatus();
      BrickBench.running = false;
    }
  }

  /** Reopens the last report (survives app restarts). */
  public static showLast(): void
  {
    let text = BrickBench.last_text;
    if ( !text )
      try { text = localStorage.getItem( BrickBench.TEXT_KEY ) ?? ""; } catch { /* storage unavailable */ }
    BrickBench.showReport( text || "No brick bench report yet — tap RUN first." );
  }

  private static formatReport( geometry: Record<string, any>[], reports: Record<string, any>[] ): string
  {
    const k = ( value: number ) => value >= 1000 ? `${round( value / 1000, 1 )}k` : `${Math.round( value )}`;
    const pad = ( value: unknown, width: number ) => String( value ).padEnd( Math.max( width, 12 ) );
    const by_name = new Map( reports.map( report => [ report.name, report ] ) );
    const renderer = Renderer.renderer;
    const lines: string[] = [];

    lines.push( "=== BRICK vs PLY BENCH ===" );
    lines.push( `date: ${new Date().toISOString().replace( "T", " " ).slice( 0, 19 )}` );
    lines.push( `device: ${navigator.userAgent}` );
    lines.push( `screen: ${innerWidth}x${innerHeight} @${devicePixelRatio}  canvas: ${renderer?.domElement.width}x${renderer?.domElement.height}` );
    const graphics = ( window as any ).__perf?.graphics?.();
    const profile = graphics?.profile ?? {};
    lines.push( `graphics: ${graphics?.quality ?? "?"}  msaa=${profile.msaa}  shadows=${profile.shadows} ${profile.shadow_map_size ?? ""}` );
    lines.push( "" );
    lines.push( "each line: BRICK | PLY" );

    for ( const [ brick_name, ply_name ] of PAIRS )
    {
      const brick = by_name.get( brick_name );
      const ply = by_name.get( ply_name );
      if ( !brick || !ply )
        continue;

      lines.push( "" );
      lines.push( `--- ${brick_name.replace( "BRICK ", "" )} (${brick.blocks} blocks) ---` );
      lines.push( `idle  fps   ${pad( brick.idle.fps, 8 )}| ${ply.idle.fps}` );
      lines.push( `idle  p99   ${pad( `${brick.idle.dt?.p99} ms`, 8 )}| ${ply.idle.dt?.p99} ms` );
      lines.push( `fire  fps   ${pad( brick.fire.fps, 8 )}| ${ply.fire.fps}` );
      lines.push( `fire  p99   ${pad( `${brick.fire.dt?.p99} ms`, 8 )}| ${ply.fire.dt?.p99} ms` );
      lines.push( `fire  spike ${pad( `${brick.fire.spikes} (${brick.fire.spike_pct}%)`, 8 )}| ${ply.fire.spikes} (${ply.fire.spike_pct}%)` );
      lines.push( `gl    ms    ${pad( `${brick.idle.gl}/${brick.fire.gl}`, 8 )}| ${ply.idle.gl}/${ply.fire.gl}   (idle/fire)` );
      lines.push( `phys  ms    ${pad( brick.fire.phys, 8 )}| ${ply.fire.phys}` );
      lines.push( `tris  frame ${pad( `${k( brick.idle.tris_mean )}/${k( brick.fire.tris_max )}`, 8 )}| ${k( ply.idle.tris_mean )}/${k( ply.fire.tris_max )}   (idle/peak)` );
      lines.push( `draw  calls ${pad( brick.idle.calls_mean, 8 )}| ${ply.idle.calls_mean}` );
      lines.push( `build ms    ${pad( `${brick.create_mesh_ms}`, 8 )}| ${ply.create_mesh_ms}   (all meshes at load)` );
    }

    lines.push( "" );
    lines.push( "--- per block: tris BRICK | PLY ---" );
    for ( const row of geometry )
      lines.push( `${pad( row.size, 4 )} ${pad( row.brick_tris, 6 )}| ${row.ply_tris}` );

    return lines.join( "\n" );
  }

  private static showStatus( text: string ): void
  {
    if ( !BrickBench.status_el )
    {
      const el = document.createElement( "div" );
      el.style.cssText = "position:fixed;left:8px;right:8px;top:8px;z-index:2147483646;padding:8px 10px;"
        + "background:rgba(0,0,0,0.75);color:#ffd200;font:700 13px monospace;white-space:pre-wrap;"
        + "border-radius:8px;pointer-events:none;";
      document.body.appendChild( el );
      BrickBench.status_el = el;
    }
    BrickBench.status_el.textContent = text;
    BrickBench.status_el.style.display = "block";
  }

  private static hideStatus(): void
  {
    if ( BrickBench.status_el )
      BrickBench.status_el.style.display = "none";
  }

  private static hideReport(): void
  {
    BrickBench.report_el?.remove();
    BrickBench.report_el = null;
  }

  private static showReport( text: string ): void
  {
    BrickBench.hideReport();

    const root = document.createElement( "div" );
    root.style.cssText = "position:fixed;inset:8px;z-index:2147483647;display:flex;flex-direction:column;"
      + "background:rgba(10,10,20,0.94);border:2px solid #ffd200;border-radius:10px;padding:8px;";

    const pre = document.createElement( "pre" );
    pre.textContent = text;
    pre.style.cssText = "flex:1;margin:0;overflow:auto;color:#fff;font:12px/1.35 monospace;"
      + "white-space:pre-wrap;word-break:break-word;user-select:text;-webkit-user-select:text;";

    const bar = document.createElement( "div" );
    bar.style.cssText = "display:flex;gap:8px;margin-top:8px;";
    const button = ( label: string, action: ( el: HTMLButtonElement ) => void ) =>
    {
      const el = document.createElement( "button" );
      el.type = "button";
      el.textContent = label;
      el.style.cssText = "flex:1;padding:12px 6px;border:2px solid #000;border-radius:8px;"
        + "background:#ffd200;color:#000;font:700 13px Arial,sans-serif;";
      el.addEventListener( "click", () => action( el ) );
      bar.appendChild( el );
    };

    button( "COPY", el => void BrickBench.copyText( text ).then( ok => { el.textContent = ok ? "COPIED" : "COPY FAILED"; } ) );
    button( "SAVE TXT", el => void BrickBench.saveText( text ).then( ok => { el.textContent = ok ? "SAVED" : "SAVE FAILED"; } ) );
    button( "CLOSE", () => BrickBench.hideReport() );

    root.append( pre, bar );
    document.body.appendChild( root );
    BrickBench.report_el = root;
  }

  private static async copyText( text: string ): Promise<boolean>
  {
    try
    {
      await navigator.clipboard.writeText( text );
      return true;
    }
    catch { /* falls back to execCommand below */ }

    const area = document.createElement( "textarea" );
    area.value = text;
    area.style.cssText = "position:fixed;top:0;left:0;opacity:0;";
    document.body.appendChild( area );
    area.select();
    const ok = document.execCommand( "copy" );
    area.remove();
    return ok;
  }

  /** Same route as PerfSession SAVE: native share sheet on device, a download in a browser. */
  private static async saveText( text: string ): Promise<boolean>
  {
    const cap = ( window as any ).Capacitor;
    if ( cap?.isNativePlatform?.() )
    {
      const share = cap.Plugins?.UtilityPlugin?.shareLogs;
      if ( !share )
        return false;
      try { await share( { text, prefix: "brick_bench" } ); return true; }
      catch ( error ) { console.warn( `${TAG} share failed`, error ); return false; }
    }

    const url = URL.createObjectURL( new Blob( [ text ], { type: "text/plain;charset=utf-8" } ) );
    const a = document.createElement( "a" );
    a.href = url;
    a.download = `brick_bench_${Date.now()}.txt`;
    a.click();
    setTimeout( () => URL.revokeObjectURL( url ), 1000 );
    return true;
  }

  /** Every BRICK/PLY pair back to back, each scene loaded cold. */
  public static async compare( options: RunOptions = {} ): Promise<Record<string, unknown>[]>
  {
    BrickBench.history = [];
    for ( const pair of PAIRS )
      for ( const name of pair )
        await BrickBench.run( name, options );

    StressTest.deactivate();
    console.log( `${TAG} compare` );
    return BrickBench.table();
  }
}
